import React, { createContext, useState } from "react";

export const DrumConfigurationContext = createContext();

export const DrumConfigurationProvider = ({ children }) => {
  const [drumConfigurations, setDrumConfigurations] = useState({});

  const setDrumConfiguration = (machineIndex, newConfig) => {
    console.log(`[DrumConfigurationProvider] setDrumConfiguration - machineIndex: ${machineIndex}, newConfig:`, JSON.stringify(newConfig));
    setDrumConfigurations((prevConfigs) => {
      const updatedConfigs = { ...prevConfigs, [machineIndex]: newConfig };
      console.log(`[DrumConfigurationProvider] setDrumConfiguration - updatedConfigs state:`, JSON.stringify(updatedConfigs));
      return updatedConfigs;
    });
  };

  const getDrumConfiguration = (machineIndex) => {
    return (
      drumConfigurations[machineIndex] || {
        columnsPerRow: [],
        mergedRows: [],
        mergedRowsLeft: [],
        mergedRowsRight: [],
        options: [],
        rows: 0,
        columns: 0,
      }
    );
  };

  const countGroups = (mergedRowsSide) => {
    let groups = 0;
    const numRows = mergedRowsSide.length;

    for (let i = 0; i < numRows; i++) {
      if (i === 0 || !mergedRowsSide[i - 1]) {
        groups++;
      }
    }
    return groups;
  };

  const calculateTotalCells = (data) => {
    const {
      columnsPerRow,
      mergedRows,
      mergedRowsLeft,
      mergedRowsRight,
      columns,
    } = data;

    if (columns === 2 && mergedRowsLeft && mergedRowsRight) {
      const leftGroups = countGroups(mergedRowsLeft);
      const rightGroups = countGroups(mergedRowsRight);
      return leftGroups + rightGroups;
    } else {
      let totalCells = 0;
      const numRows = columnsPerRow.length;
      let rowIndex = 0;
      while (rowIndex < numRows) {
        if (rowIndex === 0 || !mergedRows[rowIndex - 1]) {
          const columnsInGroup = columnsPerRow[rowIndex];
          rowIndex++;
          while (rowIndex < numRows && mergedRows[rowIndex - 1]) {
            rowIndex++;
          }
          totalCells += columnsInGroup;
        } else {
          rowIndex++;
        }
      }
      return totalCells;
    }
  };

  const getNumberOfCells = (machineIndex) => {
    const config = getDrumConfiguration(machineIndex);
    return calculateTotalCells(config);
  };

  // Helper function to get base columns for a machine type
  const getBaseColumnsForMachine = (machineType) => {
    const type = machineType.toUpperCase();
    if (type.includes("D810")) return 45;
    if (type.includes("D1080")) return 60;
    if (type.includes("D540")) return 45; // Covers D540, D540 NEO, D540 Neo
    return 0; // Unknown or unsupported type
  };

  // Helper function to get load for an individual cell based on its effective dimensions
  const getIndividualCellLoad = (cellHeight, cellWidthUnit) => {
    // Round cellWidthUnit to nearest 0.5 for matching, though direct values (1, 1.5, 3) are expected
    const w = Math.round(cellWidthUnit * 2) / 2;

    if (cellHeight === 1) {
      if (w === 1) return 0.66;
      if (w === 1.5) return 1.00;
      if (w === 3) return 2.00;
    } else if (cellHeight === 2) {
      if (w === 1) return 1.33;
      if (w === 1.5) return 2.00;
      if (w === 3) return 4.00;
    } else if (cellHeight >= 3) {
      // As per table: 2x3 and 3x3 are 4.00kg.
      // For height >= 3, we apply similar logic based on width.
      if (w === 1) return 2.00; // Consistent with 1x3 (assuming 3x1 is similar to 1x3 capacity for base unit)
      if (w === 1.5) return 2.00; // Consistent with 2x1.5 (assuming 3x1.5 is not significantly more than 2x1.5)
      if (w === 3) return 4.00; // Consistent with 2x3 or 3x3
    }
    return 0; // Default for unmapped dimensions
  };

  const getSpecificCellLoad = (targetRowIndex, colIndex, machineIndex, machineType) => {
    const config = getDrumConfiguration(machineIndex);
    // Ensure machineType is provided, otherwise, this calculation cannot be accurate.
    if (!machineType) {
      console.error("getSpecificCellLoad requires machineType");
      return 0;
    }
    const { columnsPerRow, mergedRows, rows: numRows } = config;

    if (!columnsPerRow || !columnsPerRow[targetRowIndex] || targetRowIndex >= numRows) return 0;

    // 1. Calculate true height of the cell at targetRowIndex
    let cellHeight = 1;
    let topOfBlock = targetRowIndex;
    // Traverse upwards to find the start of the merged block
    while (topOfBlock > 0 && mergedRows[topOfBlock - 1]) {
      topOfBlock--;
    }
    // Calculate height from this topOfBlock by counting merged rows downwards
    // The cell at targetRowIndex is part of a block starting at topOfBlock.
    // Its height is determined by how many consecutive merges happen from topOfBlock.
    let r = topOfBlock;
    while (r < numRows - 1 && mergedRows[r]) {
      cellHeight++;
      r++;
    }

    // 2. Calculate width unit for cells in this row (targetRowIndex)
    const baseColumns = getBaseColumnsForMachine(machineType);
    if (baseColumns === 0) return 0; // Unknown machine type

    const actualColumnsInThisRow = columnsPerRow[topOfBlock];
    if (actualColumnsInThisRow === 0) return 0; // Avoid division by zero

    const cellWidthUnit = baseColumns / actualColumnsInThisRow;

    return getIndividualCellLoad(cellHeight, cellWidthUnit);
  };

  const calculateMaximumLoad = (machineIndex, machineType) => {
    const config = getDrumConfiguration(machineIndex);
    console.log(`[DrumConfigurationContext] calculateMaximumLoad - machineIndex: ${machineIndex}, machineType: ${machineType}, config:`, JSON.stringify(config));

    // Ensure machineType is provided
    if (!machineType) {
      console.error("calculateMaximumLoad requires machineType");
      return 0;
    }
    const { columnsPerRow, mergedRows, rows: numRows } = config;
    const upperMachineType = machineType.toUpperCase();
    let normalizedMachineType = "";

    // Normalize machine type for matching in supportedTypes and maxLoads
    if (upperMachineType.includes("D810NEO")) normalizedMachineType = "D810NEO";
    else if (upperMachineType.includes("D1080NEO")) normalizedMachineType = "D1080NEO";
    else if (upperMachineType.includes("D540NEO")) normalizedMachineType = "D540NEO";
    else if (upperMachineType.includes("D810")) normalizedMachineType = "D810"; // Must be after NEO checks
    else if (upperMachineType.includes("D1080")) normalizedMachineType = "D1080"; // Must be after NEO checks
    // Note: D540 (non-NEO) is handled by fallback for base cols and will now be added to maxLoads

    const supportedMachineTypesForLoadCalc = ["D540", "D810", "D1080", "D540NEO", "D810NEO", "D1080NEO"]; // Added "D540"
    // More flexible check for supported type for base column calculation
    let isSupportedForBaseCols = supportedMachineTypesForLoadCalc.some(type => upperMachineType.includes(type));
    if (!isSupportedForBaseCols) { // Fallback for simple "D540" etc.
        if (upperMachineType.includes("D540")) isSupportedForBaseCols = true;
    }
    if (!isSupportedForBaseCols) {
        console.warn(`Unsupported machine type for base column calculation: ${machineType}`);
        // return 0; // Or allow calculation if baseColumns can be found, maxLoad will cap
    }

    if (!columnsPerRow || columnsPerRow.length === 0 || !numRows || numRows === 0) return 0;

    const baseColumns = getBaseColumnsForMachine(upperMachineType); // Get base columns using the (potentially broader) upperMachineType
    if (baseColumns === 0 && isSupportedForBaseCols) { // Only error if it was expected to be supported
        console.error(`Could not determine base columns for supported machine type: ${machineType}`);
        return 0;
    }
    if (baseColumns === 0 && !isSupportedForBaseCols) { // If not supported and no base columns, can't proceed.
        return 0;
    }

    let totalCalculatedLoad = 0;

    // Iterate through rows, considering merged blocks
    for (let rIndex = 0; rIndex < numRows; /* rIndex is incremented in the loop */) {
      // Determine the actual height of the cell/block starting at rIndex
      let cellHeight = 1;
      let currentRowInBlock = rIndex; // Start of the current block
      // Count how many rows are merged downwards from rIndex
      while (currentRowInBlock < numRows - 1 && mergedRows[currentRowInBlock]) {
        cellHeight++;
        currentRowInBlock++;
      }

      // All rows from rIndex to currentRowInBlock form a single logical row of cells
      // The number of columns for this logical row is taken from the first row of the block (rIndex)
      const columnsInThisLogicalRow = columnsPerRow[rIndex];

      if (columnsInThisLogicalRow > 0) {
          const cellWidthUnit = baseColumns / columnsInThisLogicalRow;
          const individualCellLoadInBlock = getIndividualCellLoad(cellHeight, cellWidthUnit);
          totalCalculatedLoad += columnsInThisLogicalRow * individualCellLoadInBlock;
      }

      // Move to the next row after this block
      rIndex = currentRowInBlock + 1;
    }

    const maxLoads = {
      "D540": 360, // Added D540 with 360kg cap
      "D810": 540,
      "D1080": 720,
      "D540NEO": 360,
      "D810NEO": 540,
      "D1080NEO": 720,
      // Aliases for case/spacing variations if necessary, though normalization helps
      "D540neo": 360,
      "D810neo": 540,
      "D1080neo": 720,
    };

    // Use the most specific normalized machine type for maxLoads lookup
    const machineKeyForMaxLoad = normalizedMachineType || upperMachineType; // Fallback to original if not specifically normalized
    let maxLoadForMachine = maxLoads[machineKeyForMaxLoad];

    // If not found by normalized or direct upperMachineType, check common variations or default to Infinity
    if (maxLoadForMachine === undefined) {
        if (upperMachineType.includes("D540") && !upperMachineType.includes("NEO")){
            // D540 non-NEO wasn't given a specific max load.
            // If it should have one (e.g. same as D540 NEO), it should be added to maxLoads.
            // Otherwise, it implies no specific cap other than calculated. Or use Infinity.
            maxLoadForMachine = Infinity; // Or a default if specified (e.g. 360 like NEO)
        } else {
            maxLoadForMachine = Infinity; // Default for other unmapped types
        }
    }

    return Math.min(Math.round(totalCalculatedLoad * 100) / 100, maxLoadForMachine);
  };

  return (
    <DrumConfigurationContext.Provider
      value={{
        drumConfigurations,
        setDrumConfiguration,
        getDrumConfiguration,
        getNumberOfCells,
        calculateMaximumLoad,
        getSpecificCellLoad,
      }}
    >
      {children}
    </DrumConfigurationContext.Provider>
  );
};
